import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Login from "@/pages/Login";
import AdminPanel from "@/pages/AdminPanel";
import Layout from "@/components/Layout";
import Setup from "@/pages/Setup";
import Overview from "@/pages/Overview";
import Boats from "@/pages/Boats";
import Owners from "@/pages/Owners";
import Inquiries from "@/pages/Inquiries";
import SlotsGrid from "@/pages/SlotsGrid";
import PlotMap from "@/pages/PlotMap";
import PlotView from "@/pages/PlotView";
import Renewals from "@/pages/Renewals";
import FiscalBook from "@/pages/FiscalBook";
import Alerts from "@/pages/Alerts";
import Settings from "@/pages/Settings";
import Account from "@/pages/Account";
import Onboarding from "@/pages/Onboarding";
import SignReturn from "@/pages/SignReturn";

const Loader = () => (
  <div className="flex h-screen items-center justify-center text-muted-foreground" data-testid="app-loader">
    Φόρτωση...
  </div>
);

function ConfiguredApp() {
  const { data: config, isLoading } = useQuery({
    queryKey: ["config"],
    queryFn: async () => (await api.get("/config")).data,
  });
  const { data: sub, isLoading: subLoading, refetch } = useQuery({
    queryKey: ["subscription"],
    queryFn: async () => (await api.get("/subscription")).data,
  });

  if (isLoading || subLoading) return <Loader />;
  if (!sub?.active) return <Onboarding onDone={refetch} />;

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/setup" element={<Setup config={config} />} />
        <Route element={<Layout config={config} />}>
          <Route path="/" element={<Navigate to="/slots" replace />} />
          <Route path="/overview" element={<Overview />} />
          <Route path="/boats" element={<Boats />} />
          <Route path="/owners" element={<Owners />} />
          <Route path="/inquiries" element={<Inquiries />} />
          <Route path="/slots" element={<SlotsGrid />} />
          <Route path="/plot" element={<PlotMap />} />
          <Route path="/plot-view" element={<PlotView />} />
          <Route path="/renewals" element={<Renewals />} />
          <Route path="/fiscal" element={<FiscalBook />} />
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/settings" element={<Settings config={config} />} />
          <Route path="/account" element={<Account />} />
          <Route path="*" element={<Navigate to="/slots" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

function Root() {
  const { user } = useAuth();
  const [showMarina, setShowMarina] = useState(false);
  if (user === null) return <Loader />;
  if (!user) return <Login />;
  if (user.platform_role === "superadmin" && !showMarina)
    return <AdminPanel onOpenMarina={() => { window.history.replaceState(null, "", "/"); setShowMarina(true); }} />;
  return <ConfiguredApp />;
}

function App() {
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/sign/")) {
    return (
      <div className="App">
        <SignReturn />
        <Toaster position="bottom-right" richColors />
      </div>
    );
  }
  return (
    <div className="App">
      <AuthProvider>
        <Root />
      </AuthProvider>
      <Toaster position="bottom-right" richColors />
    </div>
  );
}

export default App;
